import APIService from "./api.base";

// Environment detection and URL configuration
const getServerURL = () => {
  return process.env.REACT_APP_SERVER_URL;
};

export const serverURL = getServerURL();

// Create API service instance
const api = new APIService(serverURL);

/**
 * ============================================
 * PROGRAMMING QUESTION APIs
 * ============================================
 */

/**
 * Run programming code against test cases
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Code execution payload
 * @returns {Promise<object>} - Execution results
 */
export const runCode = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/programming/run`,
    payload,
    {
      timeout: 60000,
      queueable: false,
    }
  );
};

/**
 * Run code with custom input
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Code execution payload with custom input
 * @returns {Promise<object>} - Execution results
 */
export const runCodeWithCustomInput = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/programming/run-custom-input`,
    payload,
    {
      timeout: 60000,
      queueable: false,
    }
  );
};

/**
 * Save draft code (auto-save functionality)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Draft save payload
 * @returns {Promise<object>} - Save response
 */
export const saveDraft = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/programming/draft`,
    payload,
    {
      queueable: true,
    }
  );
};

/**
 * Load saved draft
 * @param {string} assessmentId - Assessment ID
 * @param {object} query - Query parameters
 * @returns {Promise<object>} - Draft data
 */
export const loadDraft = async (assessmentId, query) => {
  return api.get(`/public/assessment/${assessmentId}/programming/draft`, {
    params: query,
  });
};

/**
 * Save editor session events (for session replay)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Session events payload
 * @returns {Promise<object>} - Save response
 */
export const saveEditorEvents = async (assessmentId, payload) => {
  return api.post(`/public/assessment/${assessmentId}/editor-events`, payload, {
    queueable: true,
  });
};

/**
 * Submit programming answer (final submission)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Submission payload
 * @returns {Promise<object>} - Submission response
 */
export const submitProgrammingAnswer = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/programming/submit`,
    payload,
    {
      queueable: true,
      timeout: 60000,
    }
  );
};

/**
 * ============================================
 * CANDIDATE RESULT APIs
 * ============================================
 */

/**
 * Update candidate result (for text answers or proctoring flags)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Update payload
 * @returns {Promise<object>} - Update response
 */
export const updateCandidateResult = async (assessmentId, payload) => {
  return api.patch(
    `/public/assessment/${assessmentId}/update-candidate-result`,
    payload,
    {
      queueable: true,
    }
  );
};

/**
 * Submit candidate result (final submission)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Submission payload
 * @returns {Promise<object>} - Submission response
 */
export const submitCandidateResult = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/submit-candidate-result`,
    payload,
    {
      queueable: true,
      timeout: 60000,
    }
  );
};

/**
 * Submit assessment (final submission)
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Submission payload
 * @returns {Promise<object>} - Submission response
 */
export const submitAssessment = async (assessmentId, payload) => {
  return api.post(
    `/public/assessment/${assessmentId}/submit-assessment`,
    payload,
    {
      queueable: true,
      timeout: 60000,
    }
  );
};

/**
 * ============================================
 * MEDIA UPLOAD APIs
 * ============================================
 */

/**
 * Use server to upload file to Azure directly (without SAS token)
 * @param {string} candidateScreeningId - Candidate assessment ID
 * @param {string} questionId - Question ID
 * @param {FormData} formData - Form data with file
 * @returns {Promise<object>} - Upload response
 */
export const generateUploadUrlOnly = async (formData) => {
  const candidateScreeningId = formData.get("candidateScreeningId");
  const questionId = formData.get("questionId");

  return api.post(
    `/public/assessment/${candidateScreeningId}/media/${questionId}/upload-azure`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }
  );
};

/**
 * Background retry for upload (placeholder)
 * @param {string} questionId - Question ID
 * @param {FormData} formData - Form data
 * @param {object} uploadData - Upload data
 * @returns {Promise<object>} - Upload result
 */
export const backgroundRetryUpload = async (
  questionId,
  formData,
  uploadData
) => {
  // Placeholder implementation - will need actual logic from assessment project
  return generateUploadUrlOnly(formData);
};

/**
 * ============================================
 * FEEDBACK APIs
 * ============================================
 */

/**
 * Save candidate feedback
 * @param {object} payload - Feedback payload
 * @returns {Promise<object>} - Save response
 */
export const saveCandidateFeedback = async (payload) => {
  return api.post("/public/assessment/save-candidate-feedback", payload);
};

/**
 * ============================================
 * ASSESSMENT INFO APIs
 * ============================================
 */

/**
 * Get assessment details
 * @param {string} assessmentId - Assessment ID
 * @returns {Promise<object>} - Assessment details
 */
export const getAssessmentDetails = async (assessmentId) => {
  return api.get(`/public/assessment/${assessmentId}`);
};

/**
 * Get candidate assessment details
 * @param {string} candidateAssessmentId - Candidate assessment ID
 * @returns {Promise<object>} - Candidate assessment details
 */
export const getCandidateAssessmentDetails = async (candidateAssessmentId) => {
  return api.get(`/public/assessment/candidate/${candidateAssessmentId}`);
};

/**
 * ============================================
 * HEARTBEAT & SESSION APIs
 * ============================================
 */

/**
 * Send session heartbeat
 * @param {string} candidateAssessmentId - Candidate assessment ID
 * @returns {Promise<object>} - Heartbeat response
 */
export const sendHeartbeat = async (candidateAssessmentId) => {
  return api.post(
    `/public/assessment/${candidateAssessmentId}/heartbeat`,
    {
      timestamp: Date.now(),
    },
    {
      timeout: 5000,
    }
  );
};

/**
 * Send heartbeat using beacon API (for page unload)
 * @param {string} candidateAssessmentId - Candidate assessment ID
 * @param {string} apiBaseUrl - Optional API base URL override
 */
export const sendHeartbeatBeacon = (
  candidateAssessmentId,
  apiBaseUrl = null
) => {
  const baseUrl = apiBaseUrl || serverURL;
  const url = `${baseUrl}/public/assessment/${candidateAssessmentId}/heartbeat`;

  try {
    navigator.sendBeacon?.(
      url,
      new Blob([JSON.stringify({ timestamp: Date.now(), isBeacon: true })], {
        type: "application/json",
      })
    );
  } catch (error) {
    console.error("Failed to send heartbeat beacon:", error);
  }
};

/**
 * Update session status
 * @param {string} assessmentId - Assessment ID
 * @param {object} payload - Session status payload
 * @returns {Promise<object>} - Update response
 */
export const updateSessionStatus = async (assessmentId, payload) => {
  return api.patch(
    `/public/assessment/${assessmentId}/session-status`,
    payload
  );
};

// Export the API service instance for advanced usage
export { api, serverURL as BASE_URL };

/**
 * ============================================
 * BACKWARD COMPATIBILITY WRAPPERS
 * ============================================
 * These functions provide backward compatibility with the old
 * handleDynamicRequest pattern while using the new unified API service
 */

/**
 * Callback-style API request (replaces handleDynamicRequest)
 * @param {string} method - HTTP method
 * @param {string} url - Full URL
 * @param {Object} requestBody - Request body
 * @param {Function} wrongResponse - Error callback
 * @param {Function} rightResponse - Success callback
 * @param {Function} onNotFound - 404 callback (optional)
 */
export const handleDynamicRequest = async (
  method,
  url,
  requestBody,
  wrongResponse,
  rightResponse,
  onNotFound = null
) => {
  return api.requestWithCallbacks(
    method,
    url,
    requestBody,
    wrongResponse,
    rightResponse,
    onNotFound
  );
};

/**
 * Callback-style API request with authorization (replaces handleDynamicRequestHeader)
 * @param {string} method - HTTP method
 * @param {string} url - Full URL
 * @param {Object} requestBody - Request body
 * @param {string} token - Authorization token
 * @param {Function} wrongResponse - Error callback
 * @param {Function} rightResponse - Success callback
 */
export const handleDynamicRequestHeader = async (
  method,
  url,
  requestBody,
  token,
  wrongResponse,
  rightResponse
) => {
  return api.requestWithAuth(
    method,
    url,
    requestBody,
    token,
    wrongResponse,
    rightResponse
  );
};

/**
 * Callback-style API request for image upload (replaces handleDynamicRequestImage)
 * Note: This is a specialized version that handles image content type
 * @param {string} method - HTTP method
 * @param {string} url - Full URL
 * @param {Object} requestBody - Request body
 * @param {string} token - Authorization token
 * @param {Function} wrongResponse - Error callback
 * @param {Function} rightResponse - Success callback
 */
export const handleDynamicRequestImage = async (
  method,
  url,
  requestBody,
  token,
  wrongResponse,
  rightResponse
) => {
  try {
    const endpoint = url.replace(serverURL, "");

    const headers = {
      "Content-Type": "image/jpeg",
    };

    if (token) {
      headers.Authorization = token;
    }

    const response = await api.request({
      endpoint,
      method,
      data: requestBody,
      headers,
      auth: false,
    });

    setTimeout(() => {
      if (rightResponse && response) {
        rightResponse(response);
      }
    }, 100);
  } catch (error) {
    setTimeout(() => {
      if (wrongResponse) {
        wrongResponse(error.response?.data || error);
      }
    }, 100);
  }
};

// Default export for backward compatibility
export default {
  runCode,
  runCodeWithCustomInput,
  saveDraft,
  loadDraft,
  saveEditorEvents,
  submitProgrammingAnswer,
  updateCandidateResult,
  submitCandidateResult,
  submitAssessment,
  generateUploadUrlOnly,
  backgroundRetryUpload,
  saveCandidateFeedback,
  getAssessmentDetails,
  getCandidateAssessmentDetails,
  sendHeartbeat,
  sendHeartbeatBeacon,
  updateSessionStatus,
  // Backward compatibility exports
  handleDynamicRequest,
  handleDynamicRequestHeader,
  handleDynamicRequestImage,
};
